import * as React from 'react'

import { LogoDigitizationSetting } from '../../../../../../__generated__/graphql/catalog/graphql'
import { useTranslation } from '../../../../../../app/_providers/TranslationProvider.client'
import { EmbroideryTextInput } from '../../../primitives/EmbroideryTextInput/EmbroideryTextInput'
import { Label } from '../../../primitives/shared-form-styles'
import { LogoColorOptions } from '../LogoColorOptions/LogoColorOptions'
import { LogoNoteField } from '../LogoNoteField/LogoNoteField'
import { LogoRightsCheckbox } from '../LogoRightsCheckbox/LogoRightsCheckbox'
import { LogoUploadField } from '../LogoUploadField/LogoUploadField'

import { EditorSection } from './styles'

const MAX_LOGO_NAME_LENGTH = 50

export type LogoDraft = {
  file: File | undefined
  name: string
  note: string
  digitizationSetting: LogoDigitizationSetting | undefined
  rightsAccepted: boolean
}

export type LogoDraftErrors = Partial<Record<'file' | 'name' | 'color' | 'rights', string>>

export type LogoDigitizationStepProps = {
  draft: LogoDraft
  onChangeDraft(draft: LogoDraft): void
  errors: LogoDraftErrors
  /** The digitization charge, already formatted. */
  setupPriceLabel?: string
  /** Shows the file from this URL, for an upload whose picked file is gone. */
  filePreviewUrl?: string
}

/**
 * Everything the digitizer needs before a logo can be embroidered.
 */
export const LogoDigitizationStep: React.FC<LogoDigitizationStepProps> = ({
  draft,
  onChangeDraft,
  errors,
  setupPriceLabel,
  filePreviewUrl,
}) => {
  const {
    embroidery: {
      modal: {
        forms: { dtcLogoEmbroideryForm: copy },
      },
    },
  } = useTranslation()

  const nameLabelId = React.useId()

  return (
    <EditorSection data-logo-step=''>
      <LogoUploadField
        priceLabel={setupPriceLabel}
        file={draft.file}
        filePreviewUrl={filePreviewUrl}
        errorMessage={errors.file}
        onSelectFile={file => onChangeDraft({ ...draft, file })}
        onRemoveFile={() => onChangeDraft({ ...draft, file: undefined })}
      />

      {draft.file !== undefined && (
        <>
          <div>
            <Label id={nameLabelId}>{copy.logoName}</Label>
            <EmbroideryTextInput
              ariaLabelledby={nameLabelId}
              errorMessage={errors.name}
              maxCharacterCount={MAX_LOGO_NAME_LENGTH}
              name='logoName'
              placeholder={copy.logoNamePlaceholder}
              value={draft.name}
              onChange={event => onChangeDraft({ ...draft, name: event.target.value })}
            />
          </div>

          <LogoNoteField
            note={draft.note}
            onChangeNote={note => onChangeDraft({ ...draft, note })}
          />

          <LogoColorOptions
            selectedSetting={draft.digitizationSetting}
            errorMessage={errors.color}
            onSelectSetting={digitizationSetting =>
              onChangeDraft({ ...draft, digitizationSetting })
            }
          />

          <LogoRightsCheckbox
            checked={draft.rightsAccepted}
            errorMessage={errors.rights}
            onChange={rightsAccepted => onChangeDraft({ ...draft, rightsAccepted })}
          />
        </>
      )}
    </EditorSection>
  )
}
